import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiPlus, FiCheck, FiTrash2, FiX } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast, ToastContainer } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Icon type casts
const FiPlusComponent = FiPlus as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiCheckComponent = FiCheck as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiTrash2Component = FiTrash2 as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiXComponent = FiX as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;

interface Segment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
  fields?: string[];
  fieldTypes?: { [key: string]: string };
}

// ==================== MAPPING FUNCTIONS ====================
const mapToDisplay = (apiName: string): string => {
  const map: { [key: string]: string } = {
    name: 'Name', email: 'Email', phone: 'Contact',
    gender: 'Gender', dob: 'DOB', location: 'Location'
  };
  return map[apiName.toLowerCase()] || apiName;
};

const getInputTypeFromRuleValue = (value: string): string => {
  const knownTypes = ['Text', 'Number', 'Calendar', 'Dropdown'];
  if (knownTypes.includes(value)) {
    return value;
  }
  return 'Text';
};

const SegmentSelection: React.FC = () => {
  const navigate = useNavigate();

  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  // const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [selectedFieldsSegment, setSelectedFieldsSegment] = useState<Segment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(7);

  // const steps = ['Segments', 'Add Audience'];

  const fetchSegments = async (page: number = 0) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments?page=${page}&size=${pageSize}`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      const mappedSegments = (data.content || []).map((seg: any) => {
        const rulesJson = seg.rulesJson || [];
        const fields: string[] = [];
        const fieldTypes: { [key: string]: string } = {};

        rulesJson.forEach((group: any) => {
          group.rules?.forEach((rule: any) => {
            if (rule.field) {
              const displayName = mapToDisplay(rule.field);
              if (!fields.includes(displayName)) {
                fields.push(displayName);
              }
              if (rule.value) {
                fieldTypes[displayName] = getInputTypeFromRuleValue(rule.value);
              } else {
                fieldTypes[displayName] = 'Text';
              }
            }
          });
        });

        return {
          id: seg.id,
          name: seg.name,
          type: seg.type,
          description: seg.description,
          rulesJson: rulesJson,
          fields: fields,
          fieldTypes: fieldTypes,
        };
      });

      setSegments(mappedSegments);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setCurrentPage(data.pageable?.pageNumber ?? page); // Safety net
    } catch (error) {
      // console.error('❌ Failed to fetch segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSegments(0);
  }, []);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      setSelectedSegmentId(null);
      fetchSegments(newPage);
    }
  };
  const deleteSegment = async (segmentId: string) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token || !segmentId) return false;

    setIsDeleting(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments/${segmentId}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete segment');

      toast.success('Segment deleted successfully!');
      setShowDeleteModal(false);
      setSelectedSegmentId(null);
      fetchSegments(currentPage);
    } catch (error) {
      toast.error('Failed to delete segment');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewAllFields = (segment: Segment, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFieldsSegment(segment);
    setShowFieldsModal(true);
  };

  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  const handleEditSegment = () => {
    if (selectedSegmentId) {
      const segment = segments.find((seg) => seg.id === selectedSegmentId);
      if (segment) {
        const encodedName = segment.name.replace(/ /g, '-');
        navigate(`/audience/segments/edit/${encodedName}`, {
          state: { segmentData: segment },
        });
      }
    }
  };

  const handleDeleteClick = () => {
    if (selectedSegmentId) setShowDeleteModal(true);
  };

  const handleAddAudience = () => {
    if (!selectedSegmentId) {
      navigate('/audience/segments/add/new-segment', { state: { step: 1, fields: [] } });
    } else {
      const segment = segments.find((seg) => seg.id === selectedSegmentId);
      if (segment) {
        const slug = segment.name.toLowerCase().replace(/ /g, '-');
        navigate(`/audience/segments/add/${slug}`, {
          state: { step: 1, fields: segment.fields || [], segmentData: segment },
        });
      }
    }
    // setStep(1);
  };

  const getPageNumbers = (): number[] => {
    const pageNumbers: number[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage < 3) {
      for (let i = 0; i < maxVisiblePages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage > totalPages - 3) {
      for (let i = totalPages - maxVisiblePages; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  if (loading && segments.length === 0) {
    return <div className="p-2 flex items-center justify-center min-h-[400px]">Loading segments...</div>;
  }

  return (
    <div className='p-2'>
      <button
        className='flex items-center text-sm font-semibold mb-4 text-[#00899E] hover:underline'
        onClick={() => navigate('/audience')}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Audience
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className='text-[22px] font-bold text-[#3F5265]'>Segments</h2>
          <p className='text-[#657584] text-[15px] mb-6'>
            Add new segment or edit pre-existing segments.
          </p>
        </div>
      </div>

      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5'>
          {/* Create New Segment */}
          <div
            className={`rounded-md p-5 shadow-sm hover:shadow-md cursor-pointer border-2 ${selectedSegmentId === null ? 'border-[#00899E] bg-[#E9F9FB]' : 'border-gray-200 bg-[#F5F8FA]'}`}
            onClick={() => setSelectedSegmentId(null)}
          >
            <h3 className='text-lg font-bold text-[#3F5265] mb-1'>Create new Segment</h3>
            <p className='text-[#657584] text-sm mb-3'>Create new segment and select fields</p>
            <div className='flex items-center mt-3'>
              <FiPlusComponent className='text-[#00899E] mr-2' />
              <span className='text-[#00899E] font-semibold text-sm'>Add Segment</span>
            </div>
          </div>

          {/* Existing Segments */}
          {segments.map((segment) => {
            const fieldCount = (segment.fields || []).length;
            const visibleFields = (segment.fields || []).slice(0, 5);

            return (
              <div
                key={segment.id}
                className={`rounded-md p-5 shadow-sm hover:shadow-md cursor-pointer border-2 ${selectedSegmentId === segment.id ? 'border-[#00899E] bg-[#E9F9FB]' : 'border-gray-200 bg-[#F5F8FA]'}`}
                onClick={() => setSelectedSegmentId(segment.id)}
              >
                <h3 className='text-lg font-bold text-[#3F5265] mb-2'>{segment.name}</h3>
                <p className='text-[#657584] text-sm mb-6'>
                  This segment has {fieldCount} Fields
                </p>

                <ul className='text-[#3F5265] text-[12px] space-y-1 mb-3'>
                  {visibleFields.map((field, i) => (
                    <li key={i} className='flex items-center'>
                      <FiCheckComponent className='text-[#00B48A] mr-1' />
                      {field}
                    </li>
                  ))}
                </ul>

                {fieldCount > 5 && (
                  <p
                    className='text-[#00899E] text-xs font-semibold mt-1 cursor-pointer hover:underline'
                    onClick={(e) => handleViewAllFields(segment, e)}
                  >
                    +{fieldCount - 5} more
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-between mt-4'>
            <span className='text-sm text-gray-500'>
              Showing {segments.length} results out of {totalElements}
            </span>
            <div className='flex items-center gap-2'>
              <button
                disabled={currentPage === 0}
                onClick={() => handlePageChange(currentPage - 1)}
                className={`px-3 py-1 border rounded text-sm ${currentPage === 0
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
              >
                Prev
              </button>

              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 border rounded text-sm ${currentPage === pageNum
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {pageNum + 1}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages - 1}
                onClick={() => handlePageChange(currentPage + 1)}
                className={`px-3 py-1 border rounded text-sm ${currentPage === totalPages - 1
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex justify-end mt-6 space-x-3'>
          <button
            className={`px-5 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 flex items-center gap-2 ${!selectedSegmentId ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleDeleteClick}
            disabled={!selectedSegmentId}
          >
            <FiTrash2Component size={16} /> Delete
          </button>

          <button
            className={`px-5 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-[#3F5265] hover:bg-gray-50 ${!selectedSegmentId ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleEditSegment}
            disabled={!selectedSegmentId}
          >
            Edit
          </button>

          <button
            className='px-5 py-2 bg-[#00899E] text-white rounded-lg text-sm font-semibold hover:bg-[#007A8A]'
            onClick={handleAddAudience}
          >
            {selectedSegmentId === null ? 'Continue' : 'Add Audience'}
          </button>
        </div>
      </div>

      {/* All Fields Modal */}
      {showFieldsModal && selectedFieldsSegment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#F5F8FA] rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="relative p-6 border-b bg-white rounded-t-xl">
              <button
                onClick={() => setShowFieldsModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <FiXComponent size={24} />
              </button>
              <h3 className="text-xl font-bold text-[#3F5265]">{selectedFieldsSegment.name}</h3>
              <p className="text-[#657584] mt-1">
                This segment has {selectedFieldsSegment.fields?.length || 0} Fields
              </p>
            </div>

            <div className="p-6" style={{ height: '280px', overflowY: 'auto' }}>
              <ul className="space-y-3">
                {(selectedFieldsSegment.fields || []).map((field, i) => (
                  <li key={i} className="flex items-center text-[#3F5265]">
                    <FiCheckComponent className="text-[#00B48A] mr-3 flex-shrink-0" />
                    <span className="text-[15px]">{field}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedSegment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold text-red-600 mb-2">Delete Segment</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>"{selectedSegment.name}"</strong>?<br />
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteSegment(selectedSegment.id)}
                disabled={isDeleting}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default SegmentSelection;